import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import { useState } from 'react';

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Check character count
    const messageField = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (messageField && messageField.value.length > 500) {
      setFormStatus('error');
      setFormMessage('留言内容超过500字符限制，请精简后重新提交。');
      return;
    }

    const honeypotField = form.querySelector('input[name="website_alt"]') as HTMLInputElement;
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      setCharCount(0);
      return;
    }

    setFormStatus('loading');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9mt98gbdkpk99j7gdog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = { code: '' }; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
        setCharCount(0);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
        setFormStatus('error');
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormMessage('感谢您的留言，我们会尽快回复！');
        } else {
          setFormMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="" id="about-contact-form" className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
            placeholder="请输入您的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
            placeholder="请输入您的邮箱"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          placeholder="请输入留言主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言
          <span className="text-foreground-400 ml-2">({charCount}/500)</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          maxLength={500}
          required
          onChange={handleTextareaChange}
          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
          placeholder="请输入您的留言内容（最多500字）"
        />
      </div>

      {/* Honeypot */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-honeypot"
      />

      <button
        type="submit"
        disabled={formStatus === 'loading'}
        className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer self-start"
      >
        {formStatus === 'loading' ? '提交中...' : '发送留言'}
      </button>

      {formStatus === 'success' && (
        <p className="text-sm text-secondary-700 bg-secondary-100 px-4 py-2.5 rounded-lg">{formMessage}</p>
      )}
      {formStatus === 'error' && (
        <p className="text-sm text-accent-700 bg-accent-100 px-4 py-2.5 rounded-lg">{formMessage}</p>
      )}
    </form>
  );
}

export default function AboutPage() {
  const stats = [
    { value: '5000+', label: '漫画收录' },
    { value: '1200万+', label: '月活跃读者' },
    { value: '98.7%', label: '读者好评率' },
    { value: '12+', label: '风格分类' },
  ];

  const reviews = [
    {
      name: '小樱',
      avatar: 'https://readdy.ai/api/search-image?query=Cute%20anime%20style%20girl%20avatar%20portrait%2C%20pink%20hair%2C%20big%20eyes%2C%20simple%20light%20background%2C%20clean%20illustration&width=80&height=80&seq=avatar-review-01&orientation=squarish',
      rating: 5,
      text: '香蕉漫画真的太好用了！分类特别清楚，找漫画特别方便。我在这里发现了好多超好看的韩漫和日漫，推荐给所有漫迷朋友们！'
    },
    {
      name: '阿杰',
      avatar: 'https://readdy.ai/api/search-image?query=Cool%20anime%20style%20boy%20avatar%20portrait%2C%20short%20dark%20hair%2C%20simple%20light%20background%2C%20clean%20illustration&width=80&height=80&seq=avatar-review-02&orientation=squarish',
      rating: 5,
      text: '排行榜功能太赞了，每周都能发现新大陆。推荐的热血漫画每一部都很好看，已经成功安利给我身边所有人了！'
    },
    {
      name: '漫画迷小王',
      avatar: 'https://readdy.ai/api/search-image?query=Friendly%20anime%20style%20young%20man%20avatar%20portrait%2C%20glasses%2C%20warm%20smile%2C%20simple%20light%20background%2C%20clean%20illustration&width=80&height=80&seq=avatar-review-03&orientation=squarish',
      rating: 5,
      text: '作为资深漫迷，我可以说香蕉漫画的推荐质量真的很高。国漫、韩漫、日漫都有覆盖，连一些小众佳作都能找到，良心推荐！'
    },
  ];

  return (
    <>
      <SEOSchema type="WebPage" data={{
        name: '关于我们 - 香蕉漫画',
        description: '香蕉漫画品牌介绍、用户口碑和联系方式。精选热门韩漫、日漫、国漫分类推荐平台。',
        url: 'https://banana-comic.com/about',
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: '香蕉漫画', url: 'https://banana-comic.com' },
      }} />
      <SEOSchema type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://banana-comic.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://banana-comic.com/about' },
        ],
      }} />

      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        {/* Hero */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20abstract%20background%20with%20soft%20curves%2C%20comic-inspired%20subtle%20patterns%2C%20warm%20orange%20and%20cream%20tones%2C%20modern%20minimal%20design%2C%20clean%20composition%2C%20no%20text%2C%20gentle%20atmosphere&width=1800&height=500&seq=about-hero-comic&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/50 via-background-950/30 to-background-950/50"></div>
          <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-4">关于香蕉漫画</h1>
            <p className="text-base text-background-200 leading-relaxed">
              为漫迷而生，致力于打造最全、最优质的漫画推荐平台
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-10 relative z-10 mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-accent-500 mb-1">{stat.value}</p>
                <p className="text-sm text-foreground-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Brand Story */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-12">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">我们的故事</h2>
            <p className="text-sm text-foreground-600">始于热爱，忠于品质</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">品牌使命</h3>
              <p className="text-sm text-foreground-700 leading-relaxed">
                香蕉漫画创立于2018年，创始团队是一群资深漫画爱好者。我们发现很多漫迷在寻找好漫画时会遇到信息分散、推荐不精准等问题，于是决定打造一个专业、全面的漫画推荐平台。
              </p>
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">核心价值观</h3>
              <p className="text-sm text-foreground-700 leading-relaxed">
                我们始终坚持"质量优先、用户为本"的原则，每一部推荐的漫画都经过编辑团队认真筛选和评估，确保为读者提供最优质的漫画阅读推荐体验。
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-lg bg-background-100">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-500 text-background-50">
                <i className="ri-shield-check-line text-xl"></i>
              </div>
              <h4 className="font-heading font-bold text-foreground-900 mb-2">品质保证</h4>
              <p className="text-xs text-foreground-600 leading-relaxed">每部推荐漫画都经过专业编辑团队多轮筛选，确保推荐质量</p>
            </div>
            <div className="text-center p-6 rounded-lg bg-background-100">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-500 text-background-50">
                <i className="ri-global-line text-xl"></i>
              </div>
              <h4 className="font-heading font-bold text-foreground-900 mb-2">全面覆盖</h4>
              <p className="text-xs text-foreground-600 leading-relaxed">韩漫、日漫、国漫全面覆盖，12大风格分类满足各类漫迷需求</p>
            </div>
            <div className="text-center p-6 rounded-lg bg-background-100">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-500 text-background-50">
                <i className="ri-heart-pulse-line text-xl"></i>
              </div>
              <h4 className="font-heading font-bold text-foreground-900 mb-2">用户至上</h4>
              <p className="text-xs text-foreground-600 leading-relaxed">持续收集用户反馈，不断优化推荐算法和使用体验</p>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section id="reviews" className="py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">用户评价</h2>
              <p className="text-sm text-foreground-600">来自漫迷的真实反馈</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <div key={review.name} className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={review.avatar} alt={review.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="font-medium text-foreground-900 text-sm">{review.name}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-secondary-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">联系我们</h2>
              <p className="text-sm text-foreground-600">有任何问题或建议？欢迎给我们留言</p>
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
              <ContactForm />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-mail-line"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900">邮箱</p>
                <p className="text-xs text-foreground-500">contact@bananacomic.com</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-wechat-line"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                <p className="text-xs text-foreground-500">香蕉漫画</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-weibo-line"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900">微博</p>
                <p className="text-xs text-foreground-500">@香蕉漫画官方</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}